// Screen: "Onboarding"
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { completeOnboarding, updateProfile } from '../apiClient';

const hardwoodFloorStyle = {
  background: `
    repeating-linear-gradient(90deg, #3d2817 0px, #4a3423 2px, #5c4033 4px, #4a3423 6px, #3d2817 8px),
    repeating-linear-gradient(0deg, transparent 0px, transparent 60px, rgba(0,0,0,0.15) 60px, rgba(0,0,0,0.15) 62px),
    linear-gradient(180deg, #4a3423 0%, #3d2817 50%, #2d1f12 100%)
  `,
  backgroundBlendMode: "overlay, overlay, normal"
};

function useIsMobile() {
  const [mobile, setMobile] = useState(window.innerWidth < 600);
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 600);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return mobile;
}

const US_REGIONS = [
  'Northeast', 'Southeast', 'Midwest', 'Southwest', 'West Coast',
  'Pacific Northwest', 'Mid-Atlantic', 'New England', 'Great Lakes',
  'Great Plains', 'Rocky Mountain', 'South'
];

const STEPS = [
  { title: 'Welcome', subtitle: 'Do you know how to play Euchre?' },
  { title: 'Euchre Basics', subtitle: 'A quick overview of Euchre' },
  { title: 'Buck Euchre Rules', subtitle: 'What makes Buck Euchre special' },
  { title: 'Practice Round', subtitle: 'Try a guided hand against AI' },
  { title: 'Set Up Profile', subtitle: 'Tell us about yourself' },
  { title: 'Ready to Play!', subtitle: "You've earned 100 Bucks!" }
];

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { token, user, updateUser } = useAuth();
  const isMobile = useIsMobile();
  const [step, setStep] = useState(0);
  const [knowsEuchre, setKnowsEuchre] = useState(null);
  const [bio, setBio] = useState(user?.bio || '');
  const [region, setRegion] = useState(user?.region || '');

  const handleNext = () => {
    if (step === 0 && knowsEuchre) {
      setStep(2); // Skip basics
    } else {
      setStep(s => Math.min(s + 1, STEPS.length - 1));
    }
  };

  const handleComplete = async () => {
    if (bio || region) {
      await updateProfile(token, { bio, region });
      updateUser({ bio, region });
    }
    const result = await completeOnboarding(token);
    if (result.success) {
      updateUser({ onboardingCompleted: true, playMoneyBalance: (user?.playMoneyBalance || 0) + (result.bonusBucks || 100) });
    }
    navigate('/home');
  };

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <div style={{ textAlign: 'center' }}>
            <img src="/BuckEuchreLogoWhite.png" alt="" style={{ width: isMobile ? 280 : 400, maxWidth: '80vw', marginBottom: 24 }} />
            <div style={{ fontSize: isMobile ? 16 : 20, marginBottom: 24, color: '#c9d1d9' }}>
              Do you know how to play Euchre?
            </div>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button onClick={() => { setKnowsEuchre(true); handleNext(); }} style={btnStyle('#238636')}>
                Yes, I know Euchre
              </button>
              <button onClick={() => { setKnowsEuchre(false); setStep(1); }} style={btnStyle('#30363d')}>
                No, teach me
              </button>
            </div>
          </div>
        );
      case 1:
        return (
          <div>
            <h2 style={{ fontSize: isMobile ? 18 : 24, marginBottom: 16 }}>Euchre Basics</h2>
            <div style={cardStyle}>
              <p style={pStyle}>Euchre is a trick-taking card game using 24 cards (9 through Ace).</p>
              <p style={pStyle}><strong>Trump:</strong> One suit is "trump" each hand. Trump cards beat all other suits.</p>
              <p style={pStyle}><strong>Bowers:</strong> The Jack of trump (Right Bower) is the highest card. The Jack of the same color (Left Bower) is second highest.</p>
              <p style={pStyle}><strong>Tricks:</strong> Each player plays one card per trick. Highest card wins the trick.</p>
              <p style={pStyle}><strong>Following suit:</strong> You must play the suit that was led if you can.</p>
            </div>
          </div>
        );
      case 2:
        return (
          <div>
            <h2 style={{ fontSize: isMobile ? 18 : 24, marginBottom: 16 }}>Buck Euchre Rules</h2>
            <div style={cardStyle}>
              <p style={pStyle}><strong>Buck Euchre</strong> is a gambling variant where every player plays for themselves.</p>
              <p style={pStyle}><strong>Calling Trump:</strong> Each player can call trump or pass. If you call, you need to win at least 3 tricks to break even.</p>
              <p style={pStyle}><strong>Play or Sit:</strong> If someone else calls trump, you choose to Play (try to win tricks) or Sit (fold and avoid risk).</p>
              <p style={pStyle}><strong>Scoring:</strong> Callers who fail to get 3 tricks get "set" (penalized). Players who sit are safe but can't win.</p>
              <p style={pStyle}><strong>Going Alone:</strong> Call "loner" to play alone against everyone for double stakes!</p>
            </div>
          </div>
        );
      case 3:
        return (
          <div>
            <h2 style={{ fontSize: isMobile ? 18 : 24, marginBottom: 16 }}>Practice Round</h2>
            <div style={cardStyle}>
              <p style={pStyle}>Try a practice game against AI opponents to get the feel of Buck Euchre.</p>
              <p style={pStyle}>You can always come back to the Tutorial page for more practice.</p>
              <div style={{ textAlign: 'center', marginTop: 16 }}>
                <button
                  onClick={() => window.open('/play?mode=free&players=3', '_blank')}
                  style={btnStyle('#238636')}
                >
                  Launch Practice Game
                </button>
              </div>
            </div>
          </div>
        );
      case 4:
        return (
          <div>
            <h2 style={{ fontSize: isMobile ? 18 : 24, marginBottom: 16 }}>Set Up Your Profile</h2>
            <div style={cardStyle}>
              <div style={{ marginBottom: 16 }}>
                <label style={labelStyle}>Bio</label>
                <textarea
                  value={bio} onChange={e => setBio(e.target.value)}
                  placeholder="Tell us about yourself..."
                  style={{ ...inputStyle, height: 80, resize: 'none' }}
                  maxLength={200}
                />
              </div>
              <div>
                <label style={labelStyle}>Region</label>
                <select value={region} onChange={e => setRegion(e.target.value)} style={inputStyle}>
                  <option value="">Select your region...</option>
                  {US_REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
            </div>
          </div>
        );
      case 5:
        return (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>&#127881;</div>
            <h2 style={{ fontSize: isMobile ? 22 : 30, marginBottom: 12 }}>You're Ready!</h2>
            <div style={{ fontSize: 14, color: '#c9d1d9', marginBottom: 20 }}>
              You've earned <span style={{ color: '#fbbf24', fontWeight: 700 }}>100 Bucks</span> to get started.
            </div>
            <div style={cardStyle}>
              <p style={pStyle}>Play against AI to practice, or join Community Play to compete with real players.</p>
              <p style={pStyle}>Check out Leaderboards, Tournaments, and Clubs from your Home page.</p>
            </div>
            <button onClick={handleComplete} style={{ ...btnStyle('#238636'), marginTop: 20, fontSize: 16, padding: '12px 32px' }}>
              Let's Play!
            </button>
          </div>
        );
      default:
        return null;
    }
  };

  const content = (
    <div style={{
      padding: isMobile ? '20px 16px 40px' : '40px 24px',
      maxWidth: 600, margin: '0 auto', position: 'relative', zIndex: 1,
      fontFamily: "'Georgia', serif", color: '#e2e8f0'
    }}>
      {/* Progress bar */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24 }}>
        {STEPS.map((_, i) => (
          <div key={i} style={{
            flex: 1, height: 4, borderRadius: 2,
            background: i <= step ? '#238636' : '#30363d'
          }} />
        ))}
      </div>

      {/* Step indicator */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 11, color: '#8b949e', textTransform: 'uppercase', marginBottom: 4 }}>
          Step {step + 1} of {STEPS.length}
        </div>
        <div style={{ fontSize: 10, color: '#484f58' }}>{STEPS[step].subtitle}</div>
      </div>

      {/* Content */}
      {renderStep()}

      {/* Navigation */}
      {step > 0 && step < 5 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24 }}>
          <button onClick={() => setStep(s => s - 1)} style={btnStyle('#30363d')}>Back</button>
          <button onClick={handleNext} style={btnStyle('#238636')}>
            {step === 4 ? 'Finish' : 'Next'}
          </button>
        </div>
      )}

      {/* Skip */}
      {step < 5 && (
        <div style={{ textAlign: 'center', marginTop: 16 }}>
          <button onClick={handleComplete} style={{
            background: 'transparent', border: 'none', color: '#484f58',
            fontSize: 12, cursor: 'pointer', textDecoration: 'underline'
          }}>Skip onboarding</button>
        </div>
      )}
    </div>
  );

  if (isMobile) {
    return (
      <div style={{ ...hardwoodFloorStyle, position: 'fixed', top: 0, left: 0, right: 0, bottom: 0 }}>
        <div style={{ height: '100%', overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
          {content}
        </div>
      </div>
    );
  }

  return (
    <div style={{ ...hardwoodFloorStyle, minHeight: '100vh', position: 'relative' }}>
      {content}
    </div>
  );
}

const btnStyle = (bg) => ({
  background: bg, color: '#e2e8f0', border: 'none', borderRadius: 8,
  padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
  fontFamily: "'Georgia', serif"
});

const cardStyle = {
  background: '#161b22', border: '1px solid #30363d', borderRadius: 10,
  padding: 20
};

const pStyle = { fontSize: 13, color: '#c9d1d9', lineHeight: 1.6, marginBottom: 10 };

const labelStyle = {
  display: 'block', fontSize: 12, color: '#8b949e', marginBottom: 4, fontWeight: 600
};

const inputStyle = {
  width: '100%', background: '#0d1117', border: '1px solid #30363d', borderRadius: 6,
  color: '#e2e8f0', padding: '8px 12px', fontSize: 13, fontFamily: "'Georgia', serif",
  boxSizing: 'border-box'
};
